import * as ExcelJS from "exceljs";
import { addJustupBranding, setJustupMetadata } from "../../../../shared/utils/excel-branding.util";
import { NMoinsPeriodRow } from "./ca-nmoins-dialog.component";

const C = {
  headerBg: "FF1B2A4A",
  headerFont: "FFFFFFFF",
  periodBg: "FFE8EDF3",
  periodFont: "FF1B2A4A",
  clientBg: "FFF5F7FA",
  border: "FFD0D5DD",
  statGreen: "FFE6FFF5",
  statPurple: "FFF0E6FF",
  titleColor: "FF1B2A4A",
  up: "FF16A34A",
  down: "FFDC2626",
};

interface Stats {
  totalN: number;
  totalNFormatted: string;
  totalN1: number;
  totalN1Formatted: string;
  globalDeltaEur: number;
  globalDeltaPct: number | null;
  globalDeltaLabel: string;
  bestGrowthLabel: string;
  bestGrowthDelta: string;
  count: number;
}

export interface CaNMoinsExcelData {
  rows: NMoinsPeriodRow[];
  stats: Stats;
  scaleLabel: string;
  timeScaleLabel: string;
}

function colLetter(col: number): string {
  let s = "";
  let c = col;
  while (c > 0) { c--; s = String.fromCharCode(65 + (c % 26)) + s; c = Math.floor(c / 26); }
  return s;
}

function cellRef(col: number, row: number): string {
  return `${colLetter(col)}${row}`;
}

function headerStyle(cell: ExcelJS.Cell): void {
  cell.font = { bold: true, size: 10, color: { argb: C.headerFont } };
  cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: C.headerBg } };
  cell.alignment = { horizontal: "center", vertical: "middle", wrapText: true };
  cell.border = { bottom: { style: "medium", color: { argb: C.headerBg } } };
}

function statStyle(cell: ExcelJS.Cell, bg: string): void {
  cell.font = { bold: true, size: 11 };
  cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: bg } };
  cell.alignment = { horizontal: "center", vertical: "middle", wrapText: true };
  const b = { style: "thin" as const, color: { argb: C.border } };
  cell.border = { top: b, bottom: b, left: b, right: b };
}

function periodStyle(cell: ExcelJS.Cell): void {
  cell.font = { bold: true, size: 10, color: { argb: C.periodFont } };
  cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: C.periodBg } };
  cell.border = {
    bottom: { style: "thin", color: { argb: C.border } },
    top: { style: "thin", color: { argb: C.border } },
  };
}

function clientStyle(cell: ExcelJS.Cell): void {
  cell.font = { size: 9, italic: true };
  cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: C.clientBg } };
  cell.border = { bottom: { style: "hair", color: { argb: C.border } } };
}

const COL = { period: 1, client: 2, caN: 3, caN1: 4, delta: 5, deltaPct: 6 };
const COL_WIDTHS = [22, 28, 16, 16, 14, 10];
const HEADERS = ["Période", "Client", "CA (€)", "CA N-1 (€)", "Δ (€)", "Δ %"];
const HEADER_ROW = 6;
const LAST_COL = COL_WIDTHS.length;

export async function exportCaNMoinsExcel(data: CaNMoinsExcelData): Promise<void> {
  const wb = new ExcelJS.Workbook();
  setJustupMetadata(wb);

  const ws = wb.addWorksheet("CA N vs N-1", {
    views: [{ state: "frozen", ySplit: HEADER_ROW, showGridLines: false }],
  });

  COL_WIDTHS.forEach((w, i) => (ws.getColumn(i + 1).width = w));

  ws.mergeCells(1, 1, 1, LAST_COL - 1);
  const title = ws.getCell(1, 1);
  title.value = `CA N vs N-1 par ${data.scaleLabel.toLowerCase()} — ${data.timeScaleLabel}`;
  title.font = { bold: true, size: 16, color: { argb: C.titleColor } };
  ws.getRow(1).height = 30;

  ws.mergeCells(3, 1, 3, 2);
  ws.mergeCells(3, 3, 3, 4);
  ws.mergeCells(3, 5, 3, 6);
  ws.getCell(3, 1).value = `CA (N) : ${data.stats.totalNFormatted}`;
  statStyle(ws.getCell(3, 1), C.statGreen);
  ws.getCell(3, 3).value = `CA (N-1) : ${data.stats.totalN1Formatted}`;
  statStyle(ws.getCell(3, 3), C.statPurple);
  ws.getCell(3, 5).value = `Évolution : ${data.stats.globalDeltaLabel}`;
  statStyle(
    ws.getCell(3, 5),
    data.stats.globalDeltaPct !== null && data.stats.globalDeltaPct >= 0 ? C.statGreen : "FFFEE2E2",
  );
  ws.getRow(3).height = 26;

  ws.mergeCells(4, 1, 4, LAST_COL);
  const bestCell = ws.getCell(4, 1);
  bestCell.value = `🏆  Meilleure progression : ${data.stats.bestGrowthLabel} — ${data.stats.bestGrowthDelta}`;
  bestCell.font = { bold: true, size: 10, color: { argb: C.titleColor }, italic: true };
  bestCell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: C.statGreen } };
  bestCell.alignment = { horizontal: "left", vertical: "middle", indent: 1 };
  ws.getRow(4).height = 22;
  ws.getRow(5).height = 8;

  HEADERS.forEach((h, i) => {
    const c = ws.getCell(HEADER_ROW, i + 1);
    c.value = h;
    headerStyle(c);
  });
  ws.getRow(HEADER_ROW).height = 22;
  ws.autoFilter = {
    from: { row: HEADER_ROW, column: 1 },
    to: { row: HEADER_ROW, column: LAST_COL },
  };

  const eurFmt = '#,##0\\ "€"';
  let row = HEADER_ROW;
  const periodRowNumbers: number[] = [];

  for (const period of data.rows) {
    const periodRowNum = ++row;
    periodRowNumbers.push(periodRowNum);

    const clientCaNRefs: string[] = [];
    const clientCaN1Refs: string[] = [];

    for (const client of period.clients) {
      const cr = ++row;
      const cRow = ws.getRow(cr);
      cRow.getCell(COL.client).value = client.denomination;
      cRow.getCell(COL.caN).value = client.caN;
      cRow.getCell(COL.caN1).value = client.caN1;
      cRow.getCell(COL.delta).value = {
        formula: `${cellRef(COL.caN, cr)}-${cellRef(COL.caN1, cr)}`,
        result: client.deltaEur,
      } as any;
      cRow.getCell(COL.deltaPct).value = {
        formula: `IF(${cellRef(COL.caN1, cr)}=0,0,(${cellRef(COL.caN, cr)}-${cellRef(COL.caN1, cr)})/${cellRef(COL.caN1, cr)})`,
        result: client.deltaPct !== null ? client.deltaPct / 100 : 0,
      } as any;

      for (let c = 1; c <= LAST_COL; c++) clientStyle(cRow.getCell(c));
      [COL.caN, COL.caN1, COL.delta].forEach((c) => {
        cRow.getCell(c).numFmt = eurFmt;
        cRow.getCell(c).alignment = { horizontal: "right" };
      });
      cRow.getCell(COL.deltaPct).numFmt = "+0.0%;-0.0%;0.0%";

      if (client.deltaEur > 0) {
        cRow.getCell(COL.delta).font = { size: 9, italic: true, color: { argb: C.up } };
        cRow.getCell(COL.deltaPct).font = { size: 9, italic: true, color: { argb: C.up } };
      } else if (client.deltaEur < 0) {
        cRow.getCell(COL.delta).font = { size: 9, italic: true, color: { argb: C.down } };
        cRow.getCell(COL.deltaPct).font = { size: 9, italic: true, color: { argb: C.down } };
      }

      clientCaNRefs.push(cellRef(COL.caN, cr));
      clientCaN1Refs.push(cellRef(COL.caN1, cr));
    }

    const pRow = ws.getRow(periodRowNum);
    pRow.getCell(COL.period).value = period.label;
    if (clientCaNRefs.length > 0) {
      pRow.getCell(COL.caN).value = { formula: `SUM(${clientCaNRefs.join(",")})`, result: period.caN } as any;
      pRow.getCell(COL.caN1).value = { formula: `SUM(${clientCaN1Refs.join(",")})`, result: period.caN1 } as any;
    } else {
      pRow.getCell(COL.caN).value = period.caN;
      pRow.getCell(COL.caN1).value = period.caN1;
    }

    pRow.getCell(COL.delta).value = {
      formula: `${cellRef(COL.caN, periodRowNum)}-${cellRef(COL.caN1, periodRowNum)}`,
      result: period.deltaEur,
    } as any;
    pRow.getCell(COL.deltaPct).value = {
      formula: `IF(${cellRef(COL.caN1, periodRowNum)}=0,0,(${cellRef(COL.caN, periodRowNum)}-${cellRef(COL.caN1, periodRowNum)})/${cellRef(COL.caN1, periodRowNum)})`,
      result: period.deltaPct !== null ? period.deltaPct / 100 : 0,
    } as any;

    for (let c = 1; c <= LAST_COL; c++) periodStyle(pRow.getCell(c));
    [COL.caN, COL.caN1, COL.delta].forEach((c) => {
      pRow.getCell(c).numFmt = eurFmt;
      pRow.getCell(c).alignment = { horizontal: "right" };
    });
    pRow.getCell(COL.deltaPct).numFmt = "+0.0%;-0.0%;0.0%";

    if (period.deltaEur > 0) {
      pRow.getCell(COL.delta).font = { bold: true, size: 10, color: { argb: C.up } };
      pRow.getCell(COL.deltaPct).font = { bold: true, size: 10, color: { argb: C.up } };
    } else if (period.deltaEur < 0) {
      pRow.getCell(COL.delta).font = { bold: true, size: 10, color: { argb: C.down } };
      pRow.getCell(COL.deltaPct).font = { bold: true, size: 10, color: { argb: C.down } };
    }
  }

  row += 2;
  const gtRow = row;
  ws.mergeCells(gtRow, 1, gtRow, COL.client);
  const gtLabel = ws.getCell(gtRow, 1);
  gtLabel.value = "TOTAL";
  gtLabel.font = { bold: true, size: 11, color: { argb: C.headerFont } };
  gtLabel.fill = { type: "pattern", pattern: "solid", fgColor: { argb: C.headerBg } };
  gtLabel.alignment = { horizontal: "right", vertical: "middle" };

  const gtCells = (col: number) => periodRowNumbers.map((r) => cellRef(col, r)).join(",");

  for (const col of [COL.caN, COL.caN1]) {
    const cell = ws.getCell(gtRow, col);
    const total = col === COL.caN ? data.stats.totalN : data.stats.totalN1;
    cell.value = periodRowNumbers.length > 0
      ? { formula: `SUM(${gtCells(col)})`, result: total } as any
      : total;
    cell.font = { bold: true, size: 11, color: { argb: C.headerFont } };
    cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: C.headerBg } };
    cell.numFmt = eurFmt;
  }

  const gtDelta = ws.getCell(gtRow, COL.delta);
  gtDelta.value = { formula: `${cellRef(COL.caN, gtRow)}-${cellRef(COL.caN1, gtRow)}`, result: data.stats.globalDeltaEur } as any;
  gtDelta.font = { bold: true, size: 11, color: { argb: C.headerFont } };
  gtDelta.fill = { type: "pattern", pattern: "solid", fgColor: { argb: C.headerBg } };
  gtDelta.numFmt = eurFmt;

  const gtDeltaPct = ws.getCell(gtRow, COL.deltaPct);
  gtDeltaPct.value = {
    formula: `IF(${cellRef(COL.caN1, gtRow)}=0,0,(${cellRef(COL.caN, gtRow)}-${cellRef(COL.caN1, gtRow)})/${cellRef(COL.caN1, gtRow)})`,
    result: data.stats.globalDeltaPct !== null ? data.stats.globalDeltaPct / 100 : 0,
  } as any;
  gtDeltaPct.font = { bold: true, size: 11, color: { argb: C.headerFont } };
  gtDeltaPct.fill = { type: "pattern", pattern: "solid", fgColor: { argb: C.headerBg } };
  gtDeltaPct.numFmt = "+0.0%;-0.0%;0.0%";
  ws.getRow(gtRow).height = 26;

  await addJustupBranding(wb, ws, LAST_COL);

  const buffer = (await wb.xlsx.writeBuffer()) as ArrayBuffer;
  const blob = new Blob([new Uint8Array(buffer)], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
  const filename = `ca-n-vs-n1-${data.scaleLabel.toLowerCase()}-${new Date().toISOString().slice(0, 10)}.xlsx`;
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.style.display = "none";
  document.body.appendChild(a);
  a.click();
  setTimeout(() => {
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }, 10_000);
}
